/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-report.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from 'tss-react/mui';

import { useBoolean, useTranslation, useDateTime } from 'hooks';

import { EDIT_REPORT, GET_REPORTS } from 'queries/reports';

import Constants from 'utils/constants';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

const initialState = {
    status: '',
    comments: '',
};

// makeStyles is deprecated in MUI v5, but still works until later upgrades
// It leads to errors below of the type "Property 'foo' does not exist on type 'ClassNameMap<never>'"
// (because createStyles now returns never to mark it as deprecated)
// With MUI v5, we should be using the new styled API or the sx prop instead.
// This may be required if we ever need to upgrade again, but hopefully we can avoid this
// (We only needed to upgrade in 2026.1 because React 19 required an upgrade to Mui v5)
// https://mui.com/material-ui/migration/migrating-from-jss/
const useStyles = makeStyles()(() => ({
    dialogContent: {
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 15,
    },
    mainContentP: {
        marginBottom: 15,
        fontSize: 13,
    },
    boldText: {
        fontWeight: 500,
        paddingRight: 4,
        fontSize: 13,
    },
}));

const EditReport = ({ onClose, open, report, studyId, template, canEdit }: any) => {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const date = useDateTime();

    const [saveReport] = useMutation(EDIT_REPORT);

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);

    const disabledButton = useBoolean(true);
    const saving = useBoolean(false);

    const statuses = [
        { id: 'PREVIEW', text: t('Preview') },
        { id: 'FINAL', text: t('final') },
        { id: 'OBSOLETE', text: t('obsolete') },
    ];

    if (report && (report.status === 'FINAL' || report.status === 'OBSOLETE')) statuses.splice(0, 1);

    useEffect(() => {
        if (open)
            setState({
                status: report?.status ?? state.status,
                comments: report?.comments ?? state.comments,
            });
    }, [open, report]);

    const close = (action?: string | any) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({});
            disabledButton.setTrue();
        }
        onClose(action);
    };

    const submit = () => {
        const errorRequired = { status: state.status === Constants.ObjectId.Empty ? t('ra:requiredFieldError') : null };
        let errorComments: any = { comments: null };
        if (state.status === 'OBSOLETE') errorComments = validateFields(state.comments, 'comments', 1200, true);

        if (errorRequired.status || errorComments.comments) {
            setErrors({
                ...errors,
                ...errorRequired,
                ...errorComments,
            });
            return;
        }

        saving.setTrue();

        saveReport({
            variables: {
                id: report?.id ?? null,
                comment: state.comments,
                status: state.status,
            },
            refetchQueries: [
                {
                    query: GET_REPORTS,
                    variables: {
                        studyId,
                        template,
                    },
                },
            ],
        })
            .then(() => {
                close(report ? 'edit' : 'add');
                saving.setFalse();
            })
            .catch(() => {
                saving.setFalse();
                close('error');
            });
    };

    const onChange = (name: string) => (event: any) => {
        disabledButton.setFalse();
        const length = 1200;

        if (name === 'status' && event.target.value === 'OBSOLETE')
            setState({
                ...state,
                comments: '',
                [name]: event.target.value,
            });
        else
            setState({
                ...state,
                [name]: event.target.value,
            });
        validate(event.target.value, name, length, false);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    return (
        <CustomDialog onClose={close} fullWidth maxWidth="md" scroll="paper" open={open} className="edit-cv-dialog">
            <DialogTitle id="edit-signatory-modal-title">
                {report
                    ? t('reports:editReportModal', { id: report?._itemId ?? report?.recordId ?? report.id })
                    : t('reports:addReport')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {report ? (
                    <p className={classes.mainContentP}>
                        <span className={classes.boldText}>{t('reports:generatedDate')}: </span>
                        {date.display(report?.created, 'DD-MMM-YYYY HH:mm UTC')}
                    </p>
                ) : null}
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <SelectWithCategories
                            showWarning={false}
                            customClassName="status"
                            fieldSelect="status"
                            onChange={onChange}
                            label={`${t('ra:status')}*`}
                            filterable={false}
                            labelField="role"
                            dataArray={statuses}
                            customId="select-outlined-type"
                            currentState={state.status}
                            error={errors.status}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <TextField
                        label={state.status === 'OBSOLETE' ? `${t('ra:comments')}*` : t('ra:comments')}
                        id="edit-report-comments"
                        fullWidth
                        multiline
                        rows={5}
                        value={state.comments}
                        onChange={onChange('comments')}
                        placeholder={t('ra:enterComments')}
                        helperText={
                            <CharactersRemaining
                                id="edit-report-dialog-comments"
                                length={state.comments.length}
                                max={1200}
                                error={errors.comments}
                            />
                        }
                        error={!!errors.comments}
                        variant="outlined"
                        color="secondary"
                    />
                </div>
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!report && 'flex-end'}`}
            >
                <ModalUpdatedInfo item={report} />
                <Button
                    className="add-signatory-btn"
                    id="edit-report-btn"
                    disabled={
                        disabledButton.value ||
                        Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                        saving.value ||
                        !canEdit
                    }
                    text={report ? 'update' : 'add'}
                    onClick={submit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditReport;
